/* eslint-disable react/prop-types */
/**
 * Iter 290zp.7b — Brainstorm-to-Proposal PDF compositor controls.
 *
 * Two components used by the workbench brainstorm/plan/estimate chat:
 *
 *   1. `<ReportInclusionChip>` — tiny read-only status chip rendered
 *      under each user bubble that has an image/PDF attachment, so
 *      the rep can see at-a-glance what each one is currently set to
 *      (`In appendix · #2`, `Inline`, `Excluded`, `Context only`). NO
 *      controls live here — everything is managed in the centralised
 *      panel below to avoid scrolling 50 messages back to find a PDF.
 *
 *   2. `<ReportAttachmentsPanel>` — ONE collapsible panel above the
 *      chat composer that lists EVERY image/PDF dropped in this
 *      session (newest first), with the full inclusion select +
 *      section input + caption display + drag-to-reorder for the
 *      appendix tier. The user can include/exclude any attachment at
 *      any time without scrolling.
 */
import { useMemo, useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FileText, Image as ImageIcon, GripVertical, ChevronDown, ChevronUp,
  Loader2, Paperclip, EyeOff, AlignLeft, FileStack, FileImage,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const INCLUSION_OPTIONS = [
  { value: "context", label: "Context only" },
  { value: "inline", label: "Inline in body" },
  { value: "appendix", label: "Appendix" },
  { value: "none", label: "Exclude" },
];

function _isReportable(msg) {
  if (!msg?.upload_id) return false;
  const ct = (msg.upload_content_type || "").toLowerCase();
  const fn = (msg.upload_filename || "").toLowerCase();
  const isPdf = ct === "application/pdf" || fn.endsWith(".pdf");
  const isImage = msg.kind === "image" || ct.startsWith("image/");
  return isImage || isPdf;
}

function _inclusionFor(msg) {
  if (msg.report_inclusion) return msg.report_inclusion;
  return _isReportable(msg) ? "appendix" : "context";
}

const CHIP_STYLE = {
  context:  { label: "Context only", cls: "border-white/10 bg-white/5 text-[#a0aabf]", Icon: AlignLeft },
  inline:   { label: "Inline",       cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200", Icon: FileImage },
  appendix: { label: "Appendix",     cls: "border-cyan-400/30 bg-cyan-400/10 text-cyan-200", Icon: FileStack },
  none:     { label: "Excluded",     cls: "border-rose-400/30 bg-rose-400/10 text-rose-200", Icon: EyeOff },
};

/* ─────────────── Read-only status chip on each bubble ─────────────── */
export function ReportInclusionChip({ msg, appendixIndex }) {
  if (!_isReportable(msg)) return null;
  const incl = _inclusionFor(msg);
  const meta = CHIP_STYLE[incl] || CHIP_STYLE.context;
  const Icon = meta.Icon;
  const label = (incl === "appendix" && Number.isInteger(appendixIndex))
    ? `${meta.label} · #${appendixIndex + 1}`
    : meta.label;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 rounded text-[9.5px] uppercase tracking-wide border ${meta.cls}`}
      data-testid={`report-inclusion-chip-${msg.id}`}
      data-inclusion={incl}
      title="Manage in the 'Proposal attachments' panel below"
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/* ─────────────── Centralised attachments panel ──────────────────────
 *
 * Lists EVERY attachment in the session (sorted: appendix-by-order
 * first, then inline, then context, then excluded). For each one:
 *   • Filename + icon (PDF vs image)
 *   • Inclusion select (PDFs hide "Inline")
 *   • Section input (appears when inclusion === "inline")
 *   • Vision caption (italic) or "Captioning…" loader for images
 *   • Drag handle on appendix rows (drag to reorder)
 *
 * The panel is collapsible — defaults open so the rep can see what's
 * going into the proposal at a glance. Survives the message poll loop
 * because order/state lives on the messages themselves.
 */
function AttachmentRow({ msg, sessionId, isAppendix, appendixIndex, onUpdate, dndProps }) {
  const [saving, setSaving] = useState(false);
  const ct = (msg.upload_content_type || "").toLowerCase();
  const isPdf = ct === "application/pdf"
    || (msg.upload_filename || "").toLowerCase().endsWith(".pdf");
  const isImage = msg.kind === "image" || ct.startsWith("image/");
  const inclusion = _inclusionFor(msg);

  const opts = isPdf
    ? INCLUSION_OPTIONS.filter((o) => o.value !== "inline")
    : INCLUSION_OPTIONS;

  const patch = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/engineering/sessions/${sessionId}/messages/${msg.id}/report`,
        updates,
      );
      onUpdate?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update attachment.");
    } finally {
      setSaving(false);
    }
  };

  const FileIcon = isPdf ? FileText : ImageIcon;
  const fileTint = isPdf ? "text-rose-300" : "text-cyan-300";

  return (
    <div
      ref={dndProps?.setNodeRef}
      style={dndProps?.style}
      className="flex flex-col gap-1 px-2 py-1.5 rounded border border-white/10 bg-white/5 hover:border-cyan-400/30 transition"
      data-testid={`attachment-row-${msg.id}`}
      data-inclusion={inclusion}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isAppendix && dndProps ? (
          <button
            type="button"
            {...(dndProps.attributes || {})}
            {...(dndProps.listeners || {})}
            className="cursor-grab active:cursor-grabbing text-[#647087] hover:text-cyan-300 touch-none shrink-0"
            aria-label="Drag to reorder"
            data-testid={`appendix-drag-${msg.id}`}
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        {isAppendix && Number.isInteger(appendixIndex) && (
          <span className="text-[10px] text-cyan-300 tabular-nums w-5 text-right shrink-0">
            #{appendixIndex + 1}
          </span>
        )}
        <FileIcon className={`w-3.5 h-3.5 shrink-0 ${fileTint}`} />
        <span
          className="text-xs text-[#e5e7eb] truncate flex-1 min-w-0"
          title={msg.upload_filename || ""}
        >
          {msg.upload_filename || "Untitled"}
        </span>
        <div className="relative shrink-0">
          <select
            value={inclusion}
            disabled={saving}
            onChange={(e) => patch({ report_inclusion: e.target.value })}
            data-testid={`report-inclusion-select-${msg.id}`}
            className="hv-input appearance-none rounded px-2 py-1 pr-6 text-[11px] font-medium cursor-pointer"
          >
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#647087]" />
        </div>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-cyan-300 shrink-0" />}
      </div>
      {inclusion === "inline" && (
        <input
          type="text"
          defaultValue={msg.report_section || ""}
          placeholder="Section heading (optional — e.g. Site overview)"
          maxLength={80}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (msg.report_section || "")) patch({ report_section: v });
          }}
          data-testid={`report-inclusion-section-${msg.id}`}
          className="hv-input ml-5 rounded px-2 py-1 text-[11px]"
        />
      )}
      {isImage && inclusion !== "none" && (
        <div className="ml-5 text-[10px]">
          {msg.vision_caption ? (
            <span
              className="italic text-cyan-200/80 line-clamp-2"
              title={msg.vision_caption}
              data-testid={`report-vision-caption-${msg.id}`}
            >
              “{msg.vision_caption}”
            </span>
          ) : (
            <span className="text-[#647087] inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Captioning…
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SortableAttachmentRow(props) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: props.msg.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <AttachmentRow
      {...props}
      dndProps={{ attributes, listeners, setNodeRef, style }}
    />
  );
}

export function ReportAttachmentsPanel({ sessionId, messages, onChanged }) {
  const [collapsed, setCollapsed] = useState(false);

  // Partition all attachments by inclusion tier.
  const { appendix, inline, context, excluded, total } = useMemo(() => {
    const all = (messages || []).filter(_isReportable);
    const appendixList = all
      .filter((m) => _inclusionFor(m) === "appendix")
      .sort((a, b) => (a.report_order ?? 0) - (b.report_order ?? 0));
    const inlineList = all
      .filter((m) => _inclusionFor(m) === "inline")
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const contextList = all
      .filter((m) => _inclusionFor(m) === "context")
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const excludedList = all
      .filter((m) => _inclusionFor(m) === "none")
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return {
      appendix: appendixList,
      inline: inlineList,
      context: contextList,
      excluded: excludedList,
      total: all.length,
    };
  }, [messages]);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  }));

  const [pendingReorder, setPendingReorder] = useState(false);
  const [orderedIds, setOrderedIds] = useState(null);

  const effectiveOrder = orderedIds ?? appendix.map((m) => m.id);
  const effectiveAppendix = effectiveOrder
    .map((id) => appendix.find((m) => m.id === id))
    .filter(Boolean);

  // Iter 290zp.9 — Always render the panel (even when empty) so the
  // user can see WHERE attachments will be managed. Previously the
  // panel hid when `total === 0`, which left users hunting for a
  // section that didn't exist yet. Empty state explains the flow.

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = effectiveOrder.indexOf(active.id);
    const newIdx = effectiveOrder.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(effectiveOrder, oldIdx, newIdx);
    setOrderedIds(next);
    setPendingReorder(true);
    try {
      await api.post(
        `/engineering/sessions/${sessionId}/messages/reorder-appendix`,
        { ordered_message_ids: next },
      );
      onChanged?.();
    } catch (err) {
      toast.error("Couldn't reorder — try again.");
      setOrderedIds(null);
    } finally {
      setPendingReorder(false);
    }
  };

  // Update helper passed to each row — patches the message list in
  // place so subsequent renders see the new state immediately (the
  // 2-s background poller will reconcile shortly after).
  const handleUpdate = (updated) => {
    onChanged?.(updated);
  };

  return (
    <div
      className="rounded-md border border-cyan-400/20 bg-cyan-400/5 mb-2"
      data-testid="report-attachments-panel"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-cyan-400/5 transition"
        data-testid="report-attachments-toggle"
      >
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-cyan-200/90">
          <Paperclip className="w-3.5 h-3.5" />
          Proposal attachments · {total}
          <span className="text-[9.5px] normal-case text-[#647087] tracking-normal ml-1">
            {appendix.length} appendix · {inline.length} inline ·{" "}
            {context.length} context · {excluded.length} excluded
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-[#647087]">
          {pendingReorder && <Loader2 className="w-3 h-3 animate-spin text-cyan-300" />}
          {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </span>
      </button>
      {!collapsed && (
        <div className="px-2 pb-2 pt-1 space-y-2">
          {total === 0 && (
            <div
              className="rounded border border-dashed border-white/15 px-3 py-3 text-center"
              data-testid="report-attachments-empty"
            >
              <p className="text-[11px] text-[#a0aabf]">
                <span className="text-cyan-200">Drop images or PDFs</span> into the chat below
                <span className="text-[#647087]"> (using the </span>
                <kbd className="px-1 py-0.5 rounded bg-white/10 text-[10px]">+</kbd>
                <span className="text-[#647087]"> button)</span>
                {" "}— they&apos;ll appear here for inclusion in the final proposal PDF.
              </p>
              <p className="text-[9.5px] text-[#647087] mt-1.5">
                Photos default to <strong>Appendix</strong> · PDFs (IQ Retail quotes, drawings, datasheets) get spliced as-is at the back.
              </p>
            </div>
          )}
          {appendix.length > 0 && (
            <div>
              <p className="text-[9.5px] uppercase tracking-wide text-cyan-300/80 mb-1">
                Appendix · spliced at end of proposal (drag to reorder)
              </p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={effectiveOrder} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1">
                    {effectiveAppendix.map((m, i) => (
                      <SortableAttachmentRow
                        key={m.id}
                        msg={m}
                        sessionId={sessionId}
                        isAppendix
                        appendixIndex={i}
                        onUpdate={handleUpdate}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
          {inline.length > 0 && (
            <div>
              <p className="text-[9.5px] uppercase tracking-wide text-emerald-300/80 mb-1">
                Inline · embedded in report body
              </p>
              <div className="space-y-1">
                {inline.map((m) => (
                  <AttachmentRow
                    key={m.id} msg={m} sessionId={sessionId}
                    isAppendix={false} onUpdate={handleUpdate}
                  />
                ))}
              </div>
            </div>
          )}
          {context.length > 0 && (
            <div>
              <p className="text-[9.5px] uppercase tracking-wide text-[#647087] mb-1">
                Context only · LLM reads but NOT in PDF
              </p>
              <div className="space-y-1">
                {context.map((m) => (
                  <AttachmentRow
                    key={m.id} msg={m} sessionId={sessionId}
                    isAppendix={false} onUpdate={handleUpdate}
                  />
                ))}
              </div>
            </div>
          )}
          {excluded.length > 0 && (
            <div>
              <p className="text-[9.5px] uppercase tracking-wide text-rose-300/70 mb-1">
                Excluded · ignored entirely
              </p>
              <div className="space-y-1">
                {excluded.map((m) => (
                  <AttachmentRow
                    key={m.id} msg={m} sessionId={sessionId}
                    isAppendix={false} onUpdate={handleUpdate}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────── Back-compat exports ──────────────────────────────────
 * The previous (iter 290zp.7) names are aliased so any in-flight
 * imports keep compiling during the FE hot-reload window. Both old
 * names render the new chip — the controls now live in the panel.
 */
export const ReportInclusionRow = ReportInclusionChip;
export const ReportAppendixOrderPanel = ReportAttachmentsPanel;
export default ReportInclusionChip;
